import { Doc, Id } from "@cvx/_generated/dataModel";
import { ExternalLink, Eye, Heart, Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { VideoEmbed } from "@/components/ui/video-embed";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useConvex } from "convex/react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

const formatNumber = (num: number) => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

const CompetitorVideoCard = ({
  video,
}: {
  video: Doc<"appCompetitorVideos">;
}) => {
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow group flex flex-col">
      <VideoEmbed url={video.videoUrl} />

      <div className="p-3 space-y-2 flex-1 flex flex-col min-h-[120px]">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground bg-purple-500/10 text-purple-600 dark:text-purple-400 px-2 py-1 rounded-md font-medium">
              Competitor
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs p-1 h-auto hover:bg-secondary transition-colors"
            onClick={() => window.open(video.videoUrl, "_blank")}
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>

        {video.title && (
          <p className="text-sm text-foreground line-clamp-2 leading-relaxed">
            {video.title}
          </p>
        )}

        <div className="flex flex-wrap gap-2 mt-auto">
          {video.likes !== undefined && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded">
              <span>❤️</span>
              <span>{formatNumber(video.likes)}</span>
            </div>
          )}
          {video.comments !== undefined && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded">
              <span>💬</span>
              <span>{formatNumber(video.comments)}</span>
            </div>
          )}
          {video.shares !== undefined && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded">
              <span>📤</span>
              <span>{formatNumber(video.shares)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CreatorVideoCard = ({ video }: { video: Doc<"contractVideos"> }) => {
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow group flex flex-col">
      <VideoEmbed url={video.url} platform={video.platform} />

      <div className="p-3 space-y-2 flex-1 flex flex-col min-h-[120px]">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-md font-medium">
              Creator
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs p-1 h-auto hover:bg-secondary transition-colors"
            onClick={() => window.open(video.url, "_blank")}
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mt-auto">
          {video.views !== undefined && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded">
              <Eye className="h-3 w-3" />
              <span>{formatNumber(video.views)}</span>
            </div>
          )}
          {video.likes !== undefined && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded">
              <Heart className="h-3 w-3" />
              <span>{formatNumber(video.likes)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InspirationVideoCard = ({ video }: { video: string }) => {
  const convex = useConvex();
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Check if it's a URL or storage ID
  const isUrl = video.startsWith("http");

  useEffect(() => {
    if (isUrl) {
      setVideoUrl(video);
    } else {
      // It's a storage ID, fetch the URL
      setIsLoading(true);
      convex
        .query(api.creator_internal_videos.getAttachmentUrl, {
          storageId: video as Id<"_storage">,
        })
        .then((url) => {
          setVideoUrl(url || null);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [video, isUrl, convex]);

  if (!isUrl && isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden h-[200px] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!videoUrl) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow group flex flex-col">
      <VideoEmbed url={videoUrl} />

      <div className="p-3 space-y-2 flex-1 flex flex-col min-h-[120px]">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-1 rounded-md font-medium flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Featured Example
            </span>
          </div>
          {isUrl && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs p-1 h-auto hover:bg-secondary transition-colors"
              onClick={() => window.open(videoUrl, "_blank")}
            >
              <ExternalLink className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

interface AggregateVideosViewProps {
  campaign: Doc<"campaigns">;
}

export default function AggregateVideosView({
  campaign,
}: AggregateVideosViewProps) {
  const navigate = useNavigate();

  // Get competitor videos
  const { data: competitorVideos, isLoading: isCompetitorLoading } = useQuery({
    ...convexQuery(api.competitors.getCompetitorVideos, {
      appId: campaign?.appId,
      campaignId: campaign._id,
    }),
    enabled: !!campaign?._id && !!campaign?.appId,
  });

  // Get creator videos
  const { data: creatorVideos, isLoading: isCreatorLoading } = useQuery({
    ...convexQuery(api.videos.getVideosForCampaign, {
      campaignId: campaign._id,
    }),
    enabled: !!campaign?._id,
  });

  const isLoading = isCompetitorLoading || isCreatorLoading;

  // Combine and shuffle videos
  const allVideos = [
    ...(competitorVideos || []).map((v) => ({
      type: "competitor" as const,
      data: v,
    })),
    ...(creatorVideos || []).map((v) => ({
      type: "creator" as const,
      data: v,
    })),
    ...(campaign.inspirationVideos || []).map((videoUrl) => ({
      type: "inspiration" as const,
      data: videoUrl,
    })),
  ];

  // Display all videos
  const displayedVideos = allVideos;

  if (isLoading) {
    return (
      <div className="w-full py-12">
        <div className="flex flex-col items-center justify-center gap-4">
          <Loader2 className="animate-spin h-8 w-8 text-primary" />
          <p className="text-muted-foreground">Loading inspiration videos...</p>
        </div>
      </div>
    );
  }

  if (allVideos.length === 0) {
    return (
      <div className="w-full">
        <div className="rounded-xl border border-border bg-card p-8 text-center space-y-4">
          <div className="flex justify-center">
            <Sparkles className="h-12 w-12 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-primary">
            Get Inspired by Content
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Once competitor videos, featured examples, or creator content are
            available, they'll appear here to inspire your next creation.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate({ to: "/dashboard/getInspired" })}
            className="mt-4"
          >
            Explore Content Ideas
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-center">
        <div className="relative">
          <h2 className="text-2xl font-semibold text-primary flex items-center gap-2 justify-center">
            <Sparkles className="h-6 w-6" />
            Get Inspired
          </h2>
          <p className="text-muted-foreground mt-1">
            Check out viral content from competitors and other creators
          </p>
        </div>
      </div>

      <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
        {displayedVideos.map((video, index) => (
          <div key={index} className="break-inside-avoid mb-6">
            {video.type === "competitor" ? (
              <CompetitorVideoCard video={video.data} />
            ) : video.type === "inspiration" ? (
              <InspirationVideoCard video={video.data} />
            ) : (
              <CreatorVideoCard video={video.data} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
